<script setup lang="ts">
// PROPS
import { useToast } from 'primevue/usetoast'
import { useNabooContentStore } from '@/presentation/stores/content/nabooContent.ts'
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'
import CNabooContentModuleCard from '@/presentation/components/content/CNabooContentModuleCard.vue'
import { NabooContentLearningGrain } from '@/core/domain/nabooContent/NabooContent.ts'
import PTag from 'primevue/tag'
import CBaseHeader from '@/presentation/components/base/CBaseHeader.vue'
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'

const props = defineProps<{
  courseId: number
  moduleId: number
}>()

// SERVICES
const toast = useToast()

// STORES
const { fetchNabooContentModule } = useNabooContentStore()
const { currentLearningModule } = storeToRefs(useNabooContentStore())

const breadcrumbs = computed(() => {
  return [
    {
      name: 'content',
      link: true,
      title: 'Liste des contenus',
      icon: 'pi pi-home'
    }
  ]
})

onMounted(async () => {
  try {
    await fetchNabooContentModule(props.courseId, props.moduleId)
  } catch (error) {
    if (error instanceof Error) {
      toast.add({ severity: 'error', summary: 'Erreur', detail: error.message })
    } else {
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Une erreur est survenue, merci de contacter le support'
      })
    }
  }
})
</script>

<template>
  <c-base-header
    :title="`Contenu du module : ${currentLearningModule?.title}`"
    :display-breadcrumbs="true"
    :breadcrumbs="breadcrumbs"
  />

  <div class="gap-8 overflow-y-auto p-8 pt-2">
    <div class="flex flex-col gap-8">
      <c-base-block
        v-for="grain in currentLearningModule?.learningGrains"
        :key="`grain_${grain.id}`"
        :accordion="true"
        :data="grain"
      >
        <template #title="{ data }">
          <div
            class="flex flex-initial flex-row flex-wrap content-center items-center justify-start gap-2"
          >
            <h3 class="text-primary-900 col-span-12 font-[barlow] text-2xl font-bold uppercase">
              {{ (data as NabooContentLearningGrain).title }}
            </h3>
            <p-tag severity="warn" class="min-w-max flex-1 items-center text-xs">
              Tâche {{ (data as NabooContentLearningGrain).order.toString()?.padStart(3, '0') }}
            </p-tag>
          </div>
        </template>
        <div class="grid grid-cols-12 justify-start gap-4">
          <c-naboo-content-module-card
            v-for="child in grain.children"
            :key="`card_${child.id}_${child.order}`"
            :children="child"
            :grain="grain as NabooContentLearningGrain"
          />
        </div>
      </c-base-block>
    </div>
  </div>
</template>
